import { cloneElement, isValidElement, useState, type ReactElement, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCreateLead } from "@/hooks/useLeads";
import { useServiceTypes } from "@/hooks/useBudgets";

interface QuickCreateLeadDialogProps {
  /** Called with the new lead id. When omitted, the dialog navigates to the lead page. */
  onCreated?: (id: string) => void;
  /** Custom trigger element; it just needs to accept an `onClick`. */
  trigger?: ReactNode;
  defaultOpen?: boolean;
}

const SOURCES = [
  { value: "admin_manual", label: "Manual (Admin)" },
  { value: "contact_form", label: "Formulário de contato" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "indicacao", label: "Indicação" },
  { value: "instagram", label: "Instagram" },
  { value: "linkedin", label: "LinkedIn" },
];

const fieldClass = "h-10 rounded-[10px] bg-muted border-border text-[13.5px]";
const labelClass = "text-xs font-semibold text-muted-foreground";

export function QuickCreateLeadDialog({
  onCreated,
  trigger,
  defaultOpen = false,
}: QuickCreateLeadDialogProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [source, setSource] = useState("admin_manual");
  const [serviceTypeId, setServiceTypeId] = useState<string>("");
  const createLead = useCreateLead();
  const navigate = useNavigate();
  const { data: serviceTypes = [] } = useServiceTypes();

  const emailInvalid = email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || emailInvalid) return;

    const selectedSt = serviceTypes.find((s) => s.id === serviceTypeId);

    let result: { id: string };
    try {
      result = await createLead.mutateAsync({
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        message: message.trim() || "(sem descrição)",
        project_type: selectedSt?.name || undefined,
        source,
      });
    } catch (e) {
      toast.error("Erro ao criar lead: " + (e instanceof Error ? e.message : "tente novamente"));
      return;
    }

    toast.success("Lead criado!");
    if (onCreated) onCreated(result.id);
    else navigate(`/admin/leads/${result.id}`);

    setName("");
    setEmail("");
    setPhone("");
    setMessage("");
    setSource("admin_manual");
    setServiceTypeId("");
    setOpen(false);
  };

  return (
    <>
      {trigger && isValidElement(trigger) ? (
        cloneElement(trigger as ReactElement<{ onClick?: () => void }>, {
          onClick: () => setOpen(true),
        })
      ) : (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="shrink-0"
          onClick={() => setOpen(true)}
          title="Criar novo lead"
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[520px] gap-0 overflow-hidden rounded-[18px] p-0">
          <DialogHeader className="space-y-1 border-b border-border px-6 pb-4 pt-[22px] text-left">
            <DialogTitle className="text-[17px] font-bold tracking-[-0.02em]">
              Novo lead
            </DialogTitle>
            <DialogDescription className="text-[12.5px]">
              Cadastro rápido — você completa os dados depois.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 px-6 py-[22px] sm:grid-cols-2">
              <div className="flex flex-col gap-[7px]">
                <Label htmlFor="new-lead-nome" className={labelClass}>
                  Nome *
                </Label>
                <Input
                  id="new-lead-nome"
                  className={fieldClass}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nome do cliente"
                  required
                  autoFocus
                />
              </div>
              <div className="flex flex-col gap-[7px]">
                <Label htmlFor="new-lead-telefone" className={labelClass}>
                  Telefone
                </Label>
                <Input
                  id="new-lead-telefone"
                  className={fieldClass}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div className="flex flex-col gap-[7px]">
                <Label htmlFor="new-lead-e-mail" className={labelClass}>
                  E-mail
                </Label>
                <Input
                  id="new-lead-e-mail"
                  type="email"
                  className={`${fieldClass} ${emailInvalid ? "border-bad bg-bad-soft" : ""}`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@exemplo.com (opcional)"
                />
                {emailInvalid && (
                  <span className="text-[11.5px] font-semibold text-bad">E-mail inválido</span>
                )}
              </div>
              <div className="flex flex-col gap-[7px]">
                <Label htmlFor="new-lead-origem" className={labelClass}>
                  Origem
                </Label>
                <Select value={source} onValueChange={setSource}>
                  <SelectTrigger id="new-lead-origem" className={fieldClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {serviceTypes.length > 0 && (
                <div className="flex flex-col gap-[7px] sm:col-span-2">
                  <Label htmlFor="new-lead-tipo-de-servico" className={labelClass}>
                    Tipo de serviço
                  </Label>
                  <Select value={serviceTypeId} onValueChange={setServiceTypeId}>
                    <SelectTrigger id="new-lead-tipo-de-servico" className={fieldClass}>
                      <SelectValue placeholder="Selecionar (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((st) => (
                        <SelectItem key={st.id} value={st.id}>
                          {st.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex flex-col gap-[7px] sm:col-span-2">
                <Label htmlFor="new-lead-necessidade" className={labelClass}>
                  Necessidade
                </Label>
                <Textarea
                  id="new-lead-necessidade"
                  className="min-h-[74px] rounded-[10px] border-border bg-muted text-[13.5px]"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="O que o cliente precisa resolver…"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-[9px] border-t border-border px-6 py-4">
              <Button
                type="button"
                variant="outline"
                className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
                onClick={() => setOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
                disabled={createLead.isPending || emailInvalid}
              >
                {createLead.isPending ? "Criando..." : "Criar lead"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
